<template>
  <div :id="ids.vid" class="starfield" :class="{'debug' : debug, 'starfield-bare': bare}">
    <canvas v-show="running" :id="ids.cid" style="z-index: -1;" />
    <div class="starfield-scroll">
      <slot />
    </div>
    <StarfieldDebug
      v-show="running"
      @stop="stop"
      @start="start"
      @reset="reset"
    />
  </div>
</template>

<script>

import fastdom from "fastdom";
import mainloop from "mainloop.js";
import Stats from "stats.js";

import { useStarfieldStore } from "@/stores/starfield-store";
import { mapActions, mapState, mapWritableState } from "pinia";
import StarfieldDebug from "@/components/StarfieldDebug.vue";

export default {
  name: "Starfield",
  components: { StarfieldDebug },
  props: {
    running: {
      type: Boolean,
      required: false,
      default: true,
    },
    bare: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      stats: new Stats(),
      // mouse: {
      //   x: 0,
      // },

      // cursor: {
      //   x: 0,
      // },
    };
  },
  computed: {
    ...mapWritableState(useStarfieldStore, [
      "debug",
      "hyperspace",
      "warpFactor",
      "easing",
      "quantity",
      "starColor",
      "bgColor",
      "speed",
      "state",
    ]),
    ...mapState(useStarfieldStore, ["compColors", "compSpeed", "colors", "ids", "ratio"]),
  },
  watch: {
    running(isRunning) {
      if (isRunning) {
        this.start();
        return;
      }
      this.stop();
    },
  },
  methods: {
    ...mapActions(useStarfieldStore, { initStore: "init", resetStore: "reset" }),
    init() {
      this.initStore();
      this.starz();
      if (!this.running) {
        this.stop();
      }

      if (this.debug) {
        this.initStatsPanel();
      }
    },
    initStatsPanel() {
      this.stats.showPanel(0); // 0: fps, 1: ms, 2: mb, 3+: custom
      document.body.appendChild(this.stats.dom);
      this.stats.dom.style.right = "20px";
      this.stats.dom.style.left = "auto";
    },
    starz() {
      let sd = {
        viewportWidth: 0,
        viewportHeight: 0,
        element: null,
        context: null,


        canvasWidth: 0,
        canvasHeight: 0,

        xAxis: 0,
        yAxis: 0,
        zAxis: 0,

        star: {
          colorRatio: 0,

          starsArray: [],
        },

        prevTime: 0,
      };

      let dom = {
        measure: {
          viewport: () => {
            fastdom.measure(() => {
              sd.viewportWidth = this.$el.clientWidth;
              sd.viewportHeight = this.$el.clientHeight;

              sd.xAxis = Math.round(sd.viewportWidth / 2);
              sd.yAxis = Math.round(sd.viewportHeight / 2);
              sd.zAxis = (sd.viewportWidth + sd.viewportHeight) / 2;

              sd.star.colorRatio = 1 / sd.zAxis;

              // if (this.cursor.x === 0 || this.cursor.y === 0) {
              //   this.cursor.x = sd.xAxis
              //   this.cursor.y = sd.yAxis
              // }
              // if (this.mouse.x === 0 || this.mouse.y === 0) {
              //   this.mouse.x = this.cursor.x - sd.xAxis
              //   this.mouse.y = this.cursor.y - sd.yAxis
              // }
            });

            fastdom.catch = function () {
              return;
            };
          },
        },
      };

      let starFunctions = {
        viewport() {
          dom.measure.viewport();
        },

        canvas: () => {
          dom.measure.viewport();

          let c = document.getElementById(this.ids.cid);
          sd.context = c.getContext("2d");

          sd.context.canvas.width = sd.viewportWidth;
          sd.context.canvas.height = sd.viewportHeight;

          sd.context.fillStyle = this.colors.fill;
          sd.context.strokeStyle = this.starColor;
        },

        bigBang: () => {
          let areStarsInitialized = sd.star.starsArray.length === this.quantity;
          let starsRandomized = false;

          if (sd.star.starsArray.length > 0) {
            if (sd.star.starsArray[0][0] !== 0 || sd.star.starsArray[0][0] !== Infinity) {
              starsRandomized = true;
            }
          }

          let boom = () => {
            sd.star.starsArray = new Array(this.quantity);

            for (let i = 0; i < this.quantity; i++) {
              sd.star.starsArray[i] = new Array(8);

              sd.star.starsArray[i][0] = (Math.random() * sd.viewportWidth * 2) - (sd.xAxis * 2);
              sd.star.starsArray[i][1] = (Math.random() * sd.viewportHeight * 2) - (sd.yAxis * 2);
              sd.star.starsArray[i][2] = Math.round(Math.random() * sd.zAxis);
              sd.star.starsArray[i][3] = 0;
              sd.star.starsArray[i][4] = 0;
              sd.star.starsArray[i][5] = 0; // prev x
              sd.star.starsArray[i][6] = 0; // prev y
              sd.star.starsArray[i][7] = true; // test var
            }
          };

          if (!areStarsInitialized) {
            boom();
          } else if (areStarsInitialized && !starsRandomized) {
            boom();
          }
        },

        resize: () => {
          if (this.resizing) {
            let oldStar = sd.star;
            dom.measure.viewport();

            sd.canvasWidth = sd.context.canvas.width;
            sd.canvasHeight = sd.context.canvas.height;

            if (sd.canvasWidth !== sd.viewportWidth || sd.canvasHeight !== sd.viewportHeight) {
              sd.xAxis = Math.round(sd.viewportWidth / 2);
              sd.yAxis = Math.round(sd.viewportHeight / 2);
              sd.zAxis = (sd.viewportWidth + sd.viewportHeight) / 2;

              sd.star.colorRatio = 1 / sd.zAxis;

              let rw = sd.viewportWidth / sd.canvasWidth;
              let rh = sd.viewportHeight / sd.canvasHeight;

              sd.context.canvas.width = sd.viewportWidth;
              sd.context.canvas.height = sd.viewportHeight;

              if (!sd.star.starsArray.length) {
                starFunctions.bigBang();
              } else {
                for (var i = 0; i < this.quantity; i++) {
                  sd.star.starsArray[i];
                  sd.star.starsArray[i][0] = oldStar.starsArray[i][0] * rw;
                  sd.star.starsArray[i][1] = oldStar.starsArray[i][1] * rh;

                  sd.star.starsArray[i][3] = sd.xAxis + ((sd.star.starsArray[i][0] / sd.star.starsArray[i][2]) * this.ratio.computed);
                  sd.star.starsArray[i][4] = sd.yAxis + ((sd.star.starsArray[i][1] / sd.star.starsArray[i][2]) * this.ratio.computed);
                }
              }

              sd.context.fillStyle = this.colors.fill;
              sd.context.strokeStyle = this.starColor;

              this.resizing = false;
            }
          }
        },

        anim: {
          init() {
            mainloop
              .setBegin(starFunctions.anim.begin)
              .setUpdate(starFunctions.anim.update)
              .setDraw(starFunctions.anim.draw)
              .setEnd(starFunctions.anim.end);
          },

          start: () => {
            mainloop.start();
            this.resizing = true;
          },

          begin: () => {
            if (this.debug) {
              this.stats.begin();
            }

            starFunctions.resize();

            if (sd.prevTime === 0) {
              sd.prevTime = Date.now();
            }

            this.state.running = mainloop.isRunning();
          },

          update: () => {
            const quantity = this.quantity;
            const speed = this.compSpeed.lyph;
            const ratio = this.ratio.computed;
            if (sd.star.starsArray.length > 0) {
              for (let index = 0; index < quantity; index++) {
                sd.star.starsArray[index][7] = true;
                sd.star.starsArray[index][5] = sd.star.starsArray[index][3];
                sd.star.starsArray[index][6] = sd.star.starsArray[index][4];

                if (sd.star.starsArray[index][0] > sd.xAxis << 1) {
                  sd.star.starsArray[index][0] -= sd.viewportWidth << 1;
                  sd.star.starsArray[index][7] = false;
                }
                if (sd.star.starsArray[index][0] < -sd.xAxis << 1) {
                  sd.star.starsArray[index][0] += sd.viewportWidth << 1;
                  sd.star.starsArray[index][7] = false;
                }

                if (sd.star.starsArray[index][1] > sd.yAxis << 1) {
                  sd.star.starsArray[index][1] -= sd.viewportHeight << 1;
                  sd.star.starsArray[index][7] = false;
                }
                if (sd.star.starsArray[index][1] < -sd.yAxis << 1) {
                  sd.star.starsArray[index][1] += sd.viewportHeight << 1;
                  sd.star.starsArray[index][7] = false;
                }

                sd.star.starsArray[index][2] -= speed;
                if (sd.star.starsArray[index][2] > sd.zAxis) {
                  sd.star.starsArray[index][2] -= sd.zAxis;
                  sd.star.starsArray[index][7] = false;
                }
                if (sd.star.starsArray[index][2] < 0) {
                  sd.star.starsArray[index][2] += sd.zAxis;
                  sd.star.starsArray[index][7] = false;
                }

                sd.star.starsArray[index][3] = sd.xAxis + ((sd.star.starsArray[index][0] / sd.star.starsArray[index][2]) * ratio);
                sd.star.starsArray[index][4] = sd.yAxis + ((sd.star.starsArray[index][1] / sd.star.starsArray[index][2]) * ratio);
              }
            }
          },

          draw: () => {
            const quantity = this.quantity;
            sd.context.fillStyle = this.colors.fill;
            sd.context.fillRect(0, 0, sd.viewportWidth, sd.viewportHeight);
            sd.context.strokeStyle = this.starColor;

            if (sd.star.starsArray.length) {
              for (let index = 0; index < quantity; index++) {
                if (sd.star.starsArray[index][5] > 0 &&
                  sd.star.starsArray[index][5] < sd.viewportWidth &&
                  sd.star.starsArray[index][6] > 0 &&
                  sd.star.starsArray[index][6] < sd.viewportHeight &&
                  sd.star.starsArray[index][7]) {
                  sd.context.lineWidth = (1 - (sd.star.colorRatio * sd.star.starsArray[index][2])) * 2;
                  sd.context.beginPath();
                  sd.context.moveTo(sd.star.starsArray[index][5], sd.star.starsArray[index][6]);
                  sd.context.lineTo(sd.star.starsArray[index][3], sd.star.starsArray[index][4]);
                  sd.context.stroke();
                  sd.context.closePath();
                }
              }
            }
          },

          stop: () => {
            mainloop.stop();
            this.state.running = mainloop.isRunning();
          },

          end: (fps, panic) => {
            if (this.debug) {
              this.stats.end();
            }

            let delta = Date.now() - sd.prevTime;
            if (fps < 24 && delta >= 1000) {
              this.stop();
              mainloop.resetFrameDelta();
              this.quantity = this.quantity - 25;
              this.reset();

              if (this.quantity < 64) {
                this.stop();
              }

              sd.prevTime = 0;
            }

            if (panic) {
              mainloop.resetFrameDelta();
            }
          },

          reset: () => {
            this.stop();

            starFunctions.resetDims();

            this.init();
          },

          destroy: () => {
            this.stop();

            starFunctions.resetDims();

            starFunctions = null;
            sd = null;
          },
        },

        resetDims() {
          sd.xAxis = 0;
          sd.yAxis = 0;
          sd.zAxis = 0;
          sd.viewportWidth = 0;
          sd.viewportHeight = 0;
          sd.canvasWidth = 0;
          sd.canvasHeight = 0;
        },
      };

      if (this.state.destroy) {
        this.state.destroy = false;
        starFunctions.anim.destroy();
      }

      if (this.state.stop) {
        this.state.stop = false;
        starFunctions.anim.stop();
      }

      if (this.state.reset) {
        this.state.reset = false;
        starFunctions.anim.reset();
      }

      if (this.state.init) {
        this.state.init = false;
        starFunctions.viewport();
        starFunctions.anim.init();
      }

      if (this.state.canvas) {
        this.state.canvas = false;
        starFunctions.canvas();
      }

      if (this.state.start) {
        this.state.start = false;
        starFunctions.anim.start();
      }

    },

    reset() {
      this.state.reset = true;
      this.starz();
    },

    stop() {
      this.state.stop = true;
      this.starz();
    },

    start() {
      this.state.start = true;
      this.starz();
    },
  },
  beforeCreate() {
    setTimeout(() => {
      this.init();
    }, 100);

  },
  beforeUnmount() {
    mainloop.stop();
    this.resetStore();

    this.hyperspace = false;

    this.starz();
  },
};
</script>

<style scoped>
.starfield-scroll {
  height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding-left: 40px;
  padding-right: 40px;
}
@media (max-width: 768px) {
  .starfield-scroll {
    padding-left: 8px;
    padding-right: 8px;
  }
}

.starfield-bare .starfield-scroll {
  padding-left: 0;
  padding-right: 0;
}

#Test-id {
  height: 100%;
  width: 100%;

  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
}

.starfield.debug {
  z-index: 2;
}


.starfield {
  height: 100%;
  left: 0;
  margin: 0;
  padding: 0;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 0;
}

.starfield canvas {
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
}

//@font-face {
  //font-family: StarWars;
  //src: url('@/fonts/Starjhol.ttf') format('truetype');
  //font-weight: normal;
//}

h1 {
  color: white;
  position: relative;
  text-align: center;
  font-family: 'StarWars';
}
</style>
